/**
 * Copy pentru funcționalități, română. Vezi features.en.ts pentru regulile de redactare — pe scurt:
 * întâi ce câștigă cabinetul, iar detaliul tehnic rămâne ca dovadă în spate. Fiecare afirmație a
 * fost verificată în cod, nimic despre ce nu există încă, lipsurile stau în `roadmap` și se pot
 * vota, iar deciziile luate intenționat stau în `boundaries` și nu sunt scuze.
 */
export const featuresRo = {
  indexTitle: 'Ce face easyDent pentru cabinetul tău',
  indexSubtitle:
    'Fiecare funcționalitate, ce îți schimbă din zi și, pe șleau, unde se oprește. '
    + 'Scris pentru cineva care decide dacă i se potrivește cabinetului — nu pentru o broșură.',
  groups: {
    day: 'Ziua de lucru',
    clinical: 'Clinic',
    money: 'Bani',
    growth: 'Pacienți & creștere',
    team: 'Echipă & cabinet',
    platform: 'Platformă',
  },
  backToIndex: 'Toate funcționalitățile',
  howItWorks: 'Cum funcționează',
  whatItDoes: 'Ce face',
  whoFor: 'Pentru cine este',
  boundaries: 'Unde se oprește, intenționat',
  needs: 'De ce ai nevoie pentru asta',
  comingSoon: 'În curând',
  comingSoonBlurb: 'Încă nefăcute. Spune-ne care dintre ele contează pentru tine — așa decidem ce construim mai departe.',
  wantThis: 'Vreau asta',
  wanted: 'Ai cerut asta',
  votes_one: '{{count}} persoană a cerut asta',
  votes_few: '{{count}} persoane au cerut asta',
  votes_other: '{{count}} de persoane au cerut asta',
  votesNone: 'Nimeni nu a cerut încă asta',
  notifyTitle: 'Vrei să afli când apare una dintre ele?',
  notifyPlaceholder: 'email@exemplu.ro',
  notifySubmit: 'Anunță-mă',
  notifyConsent: 'Trimiteți-mi emailuri despre noutățile easyDent. Mă pot retrage oricând din linkul aflat în orice mesaj.',
  notifyConsentRequired: 'Bifează căsuța ca să îți putem trimite emailuri.',
  notifyDone: 'Gata — te anunțăm.',
  notifyError: 'Nu a mers. Verifică adresa și încearcă din nou.',
  notifyPrivacy: 'Păstrăm adresa ta și nimic altceva, o folosim doar pentru asta și nu o dăm mai departe.',
  related: 'Similare',
  readMore: 'Detalii →',
  tryDemo: 'Vezi în demo',
  signIn: 'Autentificare',
  ctaTitle: 'Mai ușor de văzut decât de citit',
  ctaBody: 'Cabinetul demo este un cabinet real, funcțional, cu pacienți, programări și fișe deja în el. Nimic de instalat, fără card.',
  ctaButton: 'Deschide cabinetul demo',

  // ── Ziua de lucru ──────────────────────────────────────────────────────────
  scheduling: {
    title: 'Programări',
    summary: 'Un singur calendar în care lucrează toată echipa deodată, fără să suprapună vreodată două programări pe același scaun.',
    steps: [
      { title: 'Îl configurezi o dată', body: 'Scaunele, programul, ce medic lucrează unde. O jumătate de oră și nu mai revii.' },
      { title: 'Programezi de oriunde', body: 'Programează recepția sau pacientul singur, online. Intervalul este cât cere manopera.' },
      { title: 'Ziua merge singură', body: 'Confirmările și reamintirile pleacă singure. Tu te uiți la planning și tratezi pacienți.' },
    ],
    details: [
      'Două persoane de la recepție nu pot programa pe același scaun. A doua află imediat, cât pacientul este încă în fața ei.',
      'Intervalele sunt cât durează efectiv manopera, deci ziua nu o ia razna discret până la unsprezece.',
      'Pacienții anulează și reprogramează din linkul reamintirii — fără telefon, fără cont, iar locul redevine liber pe loc.',
      'Vezi un scaun, un medic sau toate locațiile deodată, dacă ai mai multe.',
      'Programările cu risc de neprezentare sunt marcate, deci reamintirea în plus ajunge la cine are nevoie, nu la toată lumea.',
      'Un cabinet din mai multe orașe păstrează ora corectă în fiecare. Nimeni nu ajunge cu o oră mai devreme.',
    ],
    audience: 'Orice cabinet cu mai mult de un scaun — mai ales unul care ține mai multe locații de la o singură recepție.',
    roadmap: {
      'recurring': 'Serii de programări recurente — programarea unui set de vizite dintr-o dată.',
      'resources': 'Camere și echipamente ca resurse rezervabile separat, pe lângă scaune și medici.',
    },
  },
  'patient-records': {
    title: 'Fișa pacientului',
    summary: 'Tot ce ține de un pacient într-un singur loc, ca nimeni să nu îl întrebe același lucru de două ori.',
    steps: [
      { title: 'Îl adaugi în câteva secunde', body: 'Un nume și un telefon sunt de ajuns ca să programezi. Restul poate aștepta până e pe scaun.' },
      { title: 'Iei anamneza înainte să vină', body: 'Trimiți un link, iar antecedentele sunt completate de acasă — le citești înainte să se așeze.' },
      { title: 'Totul se adună aici', body: 'Programări, tratamente, plăți, radiografii și documente ajung singure pe aceeași fișă.' },
    ],
    details: [
      'Un pacient fără email este un pacient normal, nu o excepție. Numărul de telefon este identificatorul.',
      'Alergiile, medicația curentă și afecțiunile cronice sunt câmpuri proprii, vizibile dintr-o privire — nu o frază pierdută în notele de anul trecut.',
      'Anamneza ajunge înaintea pacientului, deci primele cinci minute nu se duc pe hârtii.',
      'CNP-ul este verificat pe măsură ce e tastat, deci o cifră greșită este prinsă înainte să ajungă pe o factură.',
      'Datele de contact și CNP-ul sunt criptate, iar căutarea funcționează exact ca înainte.',
      'Pacienții probabil dubli sunt găsiți peste noapte și puși în fața unui om. Nu se unifică nimic pe la spatele tău.',
    ],
    audience: 'Orice cabinet. Este coloana de care atârnă tot restul.',
    roadmap: {
      'import': 'Aducerea pacienților din programul tău actual, ca nimeni să nu retasteze zece ani de fișe.',
    },
    boundaries: [
      'Pacienții dubli sunt semnalați pentru ca un om să decidă. Nu se unifică nimic automat — o unificare greșită se repară mult mai greu decât se tolerează un duplicat.',
    ],
  },
  'waiting-list': {
    title: 'Listă de așteptare',
    summary: 'O anulare încetează să fie bani pierduți. Locul liber e oferit cuiva care voia mai devreme.',
    steps: [
      { title: 'Notezi cine vrea mai repede', body: 'Pacientul care nu poate aștepta până la primul loc liber intră pe listă, cu ce are de făcut.' },
      { title: 'Cineva anulează', body: 'Locul e oferit imediat pacientului căruia chiar i se potrivește — manopera potrivită, durata potrivită.' },
      { title: 'Îl ia', body: 'O atingere pe un link și scaunul e ocupat. Fără cont, fără telefoane încrucișate, fără oră goală.' },
    ],
    details: [
      'O anulare de ultim moment se acoperă cât mai merită acoperită, în loc să devină o oră pe care nu ți-o plătește nimeni.',
      'Oferta ajunge la un pacient căruia intervalul chiar i se potrivește, deci nu înghesui o oră de lucru în douăzeci de minute.',
      'Pacienții acceptă cu o atingere pe link — și cei care nu s-au autentificat nicăieri vreodată pot lua locul.',
      'Ofertele pleacă pe rând, nu la toată lumea odată, deci nu răspunzi la șase telefoane despre același loc.',
      'Lista se golește singură după 30 de zile, deci nu se umple discret cu oameni tratați acum câteva luni.',
    ],
    audience: 'Cabinete cu timp de așteptare real, unde o anulare de ieri sunt bani pe care nu îi mai vezi.',
    roadmap: {
      'cross-branch': 'Oferirea unui loc liber la altă locație a ta, nu doar la cea cerută.',
      'priority': 'Prioritizare automată, ca lista să nu fie strict în ordinea sosirii.',
    },
  },

  // ── Clinic ─────────────────────────────────────────────────────────────────
  'dental-chart': {
    title: 'Schema dentară',
    summary: 'Toată arcada pe un singur ecran și povestea fiecărui dinte în spatele ei.',
    steps: [
      { title: 'Deschizi schema', body: 'Dinți desenați ca dinți, adult sau pediatric — nu un tabel de numere de descifrat.' },
      { title: 'Atingi ce vezi', body: 'Setezi o suprafață, adaugi o notă pe dinte. Durează cam cât să o spui cu voce tare.' },
      { title: 'Te uiți înapoi oricând', body: 'Pe ecran e schema de azi; tot istoricul e la un clic când pacientul întreabă cum s-a ajuns aici.' },
    ],
    details: [
      'Poți răspunde la „de când?” fără să deschizi un dosar de hârtie, pentru că fiecare schimbare la fiecare dinte se păstrează.',
      'Cinci suprafețe per dinte, deci notezi ce ai văzut efectiv — nu „stânga sus, pe undeva”.',
      'Gândită pentru tabletă lângă scaun, deci notezi în timp ce te uiți la pacient, nu după aceea din memorie.',
      'Zece stări care acoperă ce întâlnești: carie, obturat, coroană, extras, implant, stâlp de punte, tratament de canal, absent și inclus.',
      'Notele apar ca bule pe dintele lor, deci următorul medic le vede fără să le caute.',
      'Nu se suprascrie nimic. Ce a scris un coleg acum trei ani este exact ce a scris.',
    ],
    audience: 'Orice medic care completează schema — mai ales cabinetele unde mai mulți clinicieni tratează același pacient ani la rând.',
    roadmap: {
      'doctor-colours': 'Lucrarea fiecărui medic în culoarea lui pe schemă, ca să vezi dintr-o privire cine ce a făcut.',
      'perio': 'Cartografiere parodontală — adâncimi de pungă, indici de sângerare și retracție.',
    },
  },
  consultations: {
    title: 'Consultații & planuri de tratament',
    summary: 'Notezi vizita o dată, iar schema, devizul și nota de plată decurg din ea.',
    steps: [
      { title: 'O deschizi din calendar', body: 'Un clic din programare. Fără căutat pacientul potrivit.' },
      { title: 'Notezi manoperele', body: 'Alegi din catalogul tău, pe dinte. Prețurile vin odată cu ele și pot fi schimbate.' },
      { title: 'Devizezi sau facturezi', body: 'Marchezi lucrarea planificată sau făcută. Devizul se tipărește pe antetul tău, ca pacientul să îl ia cu el.' },
    ],
    details: [
      'Când notezi lucrarea se actualizează schema dentară, se scad materialele folosite și crește nota de plată — o singură acțiune, nu patru.',
      'Un deviz și o notă de plată nu se pot confunda, pentru că lucrarea planificată și cea făcută se numără separat.',
      'Pacientul datorează ce s-a făcut efectiv, niciodată ce doar s-a propus.',
      'Catalogul tău, prețurile tale, cotele tale de TVA și duratele tale — nu o listă preîncărcată pe care să o ocolești.',
      'Devizul pleacă cu pacientul ca PDF pe antetul tău, ceea ce e un răspuns mult mai bun decât „vă trimit pe email”.',
      'Cât îți aduce fiecare manoperă, după materiale și comision, iese din aceeași notare — vezi Rapoarte.',
    ],
    audience: 'Cabinete care devizează înainte să trateze și oricine s-a săturat să introducă aceeași vizită în trei locuri.',
    roadmap: {
      'esign': 'Planuri de tratament semnate electronic de pacient, în loc de un PDF tipărit sau trimis.',
    },
    boundaries: [
      'Catalogul ține un preț. Cât costă efectiv o manoperă se calculează în raportul de profitabilitate, din lista de materiale, comisionul medicului și timpul de scaun.',
    ],
  },
  imaging: {
    title: 'Imagistică & DICOM',
    summary: 'Radiografiile se deschid în browser, pe fișa pacientului, de pe orice calculator din clădire.',
    steps: [
      { title: 'Încarci', body: 'Tragi fișierele pe pacient. Studiile mari merg direct în stocare, fără să blocheze aplicația.' },
      { title: 'Le deschizi oriunde', body: 'Orice calculator cu browser. Nimic de instalat, nicio licență per stație.' },
      { title: 'Lucrezi pe imagine', body: 'Fereastră și contrast, zoom, deplasare, măsurare, adnotare.' },
    ],
    details: [
      'Niciun vizualizator de instalat pe fiecare calculator și nimic de reinstalat când schimbi unul.',
      'Un coleg din altă cameră deschide același studiu fără ca cineva să copieze un fișier pe stick.',
      'Măsori și adnotezi în browser, deci ce ai observat rămâne pe studiu, nu în capul tău.',
      'Radiografiile, fotografiile și documentele stau pe aceeași fișă — nu cauți un pacient în două sisteme.',
      'Imaginile sunt private: fiecare vizualizare folosește un link care expiră în câteva minute, iar fișierele nu stau pe un disc partajat.',
      'Ține fișierele în stocarea ta, dacă preferi — vezi Securitate și datele tale.',
    ],
    audience: 'Cabinete cu radiologie intraorală sau panoramică și oricine s-a săturat să instaleze un vizualizator pe fiecare stație.',
    roadmap: {
      'cbct': 'Reconstrucție 3D și CBCT. Momentan studiile se deschid ca serii 2D.',
      'direct-capture': 'Captură directă din aparatul de imagistică, în loc de încărcarea fișierelor după achiziție.',
    },
  },
  'imaging-referrals': {
    title: 'Trimiteri imagistică',
    summary: 'Trimiți pacientul la radiografie și primești studiul înapoi pe fișă, fără să alergi după nimeni.',
    steps: [
      { title: 'Îți alegi centrele', body: 'Centrele de imagistică cu care lucrezi efectiv. Doar ele pot primi trimiterile tale.' },
      { title: 'Trimiți de lângă scaun', body: 'Ce incidențe, ce dinți, de ce. Durează mai puțin decât să o scrii pe hârtie.' },
      { title: 'Studiul se întoarce', body: 'Ajunge pe fișa pacientului și pe consultația din care ai trimis. Nimeni nu cară un CD.' },
    ],
    details: [
      'Nu mai suni la centru să întrebi dacă a venit pacientul — trimiterea îți spune unde este.',
      'Dacă pacientul nu ajunge deloc, afli, ceea ce e singura cale să îl cauți înainte să se agraveze.',
      'Centrul poate refuza cu motiv, deci poți trimite pacientul în altă parte în aceeași zi.',
      'Studiul se întoarce pe fișă și pe consultație, deci este unde îl cauți, nu într-un email.',
      'Centrul vede numele pacientului, data nașterii, telefonul și de ce l-ai trimis. Nu fișele tale și nu ceilalți pacienți ai tăi.',
      'Rezultatele se marchează ca văzute când le deschide un medic, deci nimic nu stă necitit două săptămâni.',
    ],
    audience: 'Cabinete fără radiologie proprie — adică, în stomatologie, cele mai multe.',
    boundaries: [
      'Trimiterile ajung doar la centrele alese de tine. Nu există o piață deschisă — cine ajunge să citească trimiterea unui pacient trebuie să fie decizia ta, luată dinainte.',
      'Pacientul plătește direct centrului. Plata studiului nu trece prin easyDent și nu reținem nimic din ea.',
    ],
  },

  // ── Bani ───────────────────────────────────────────────────────────────────
  sterilization: {
    title: 'Trasabilitatea sterilizÄrii',
    summary: 'Răspunzi unui inspector într-un minut: această casetă, acest ciclu, acest pacient, această dată.',
    steps: [
      { title: 'Etichetezi casetele', body: 'Fiecare casetă primește un cod. Îl tipărești ca QR sau cod de bare și îl lipești.' },
      { title: 'Înregistrezi ciclul', body: 'Autoclav, program, ce casete au fost în el. Înregistrarea îl semnează pe numele tău.' },
      { title: 'Scanezi lângă scaun', body: 'Caseta este legată de pacientul la care s-a folosit. Lanțul se scrie singur.' },
    ],
    details: [
      'O inspecție devine un raport pe care îl tipărești, pe orice perioadă, în loc de o după-amiază cu un biblioraft.',
      'Înainte ca o casetă să ajungă la pacient, un singur cuvânt îți spune dacă poate fi folosită.',
      'Fiecare ciclu poartă numele celui care l-a rulat, deci răspunderea se consemnează pe loc, nu se reconstituie ulterior.',
      'Ciclurile eșuate și întrerupte se păstrează, nu dispar discret — un registru fără niciun eșec este exact cel pe care nu îl crede nimeni.',
      'O casetă care nu a fost niciodată sterilizată o spune, în loc să arate la fel ca una care a fost.',
      'Autoclavele își poartă data de service și te avertizează când a trecut.',
    ],
    audience: 'Orice cabinet care trebuie să arate unui inspector ce instrumente au atins ce pacient.',
    roadmap: {
      'labels': 'Generarea de etichete tipăribile și comanda unei imprimante de etichete, în loc să tipărești codurile singur.',
      'autoclave': 'Citirea ciclurilor din jurnalul propriu al autoclavei, în loc ca operatorul să le înregistreze.',
      'service-reminder': 'O notificare înainte ca autoclava să aibă service scadent, nu doar un avertisment după ce a trecut termenul.',
    },
  },

  payments: {
    title: 'Plăți & avansuri',
    summary: 'Încasezi la recepție, prin link sau în avans — și nu mai alergi după bani.',
    steps: [
      { title: 'La recepție', body: 'Numerar sau POS pe vizită. Chitanța pleacă singură.' },
      { title: 'Sau prin link', body: 'Trimiți un link de plată după ce a plecat. Îl atinge și plătește din parcare, dacă vrea.' },
      { title: 'Sau înainte să vină', body: 'Ceri un avans la programare, ca o neprezentare să îl coste și pe el ceva.' },
    ],
    details: [
      'Un avans la programare transformă neprezentarea din pierderea ta în decizia lui.',
      'Un link de plată face ca discuția incomodă despre soldul de luna trecută să nu mai apară aproape deloc.',
      'Recepția vede cât datorează pacientul la plecare, deci se încasează cât încă este la ghișeu.',
      'Banii ajung în contul tău, nu al nostru. Nu stăm noi la mijloc.',
      'Un pacient nu poate fi taxat de două ori pentru același lucru, orice ar face POS-ul sau rețeaua.',
      'Medicii văd fișa clinică fără bani lipiți de ea — soldul este treaba recepției.',
    ],
    audience: 'Cabinete care pierd scaune din neprezentări și oricine s-a săturat să sune pacienții pentru un sold vechi.',
    roadmap: {
      'fiscal-register': 'Integrare cu casa de marcat fiscală și imprimanta de bonuri. Momentan chitanțele sunt PDF-uri.',
      'instalments': 'Planuri de plată și rate, ca un tratament mare să fie plătit în mai multe luni.',
    },
    needs: [
      'Încasarea online înseamnă întâi o înscriere la procesatorul de card — un formular și un cont bancar. Până atunci, totul la recepție merge ca de obicei.',
    ],
  },
  invoicing: {
    title: 'Facturare & e-Factura',
    summary: 'e-Factura care trece și o cale să dovedești că ar trece înainte să te angajezi la ceva.',
    steps: [
      { title: 'Emiți', body: 'Factura vine din lucrarea deja notată. Numerotarea este secvențială, per cabinet.' },
      { title: 'O verifici la ANAF', body: 'Un buton îți spune dacă ANAF ar accepta-o — și exact la ce obiectează, dacă nu.' },
      { title: 'O depui', body: 'Odată înrolat, facturile se încarcă singure, iar rezultatul se întoarce fără să stai cu ochii pe el.' },
    ],
    details: [
      'Apeși „verifică la ANAF” azi, înainte să înrolezi ceva — poți afla că facturile tale ar fi acceptate fără să te legi întâi de noi.',
      'Respingerile îți spun ce câmp e greșit, nu doar că e ceva. Nimeni nu ghicește.',
      'Un număr de factură nu poate fi folosit de două ori, deci un duplicat nu poate apărea la inspecția fiscală.',
      'Înrolarea se face o dată, în browserul tău, cu certificatul tău. Nu ajunge la noi, iar apoi se reînnoiește singură.',
      'Facturile depuse sunt urmărite până când ANAF dă un verdict, iar arhiva semnată se păstrează ca dovadă.',
      'În afara României primești o factură PDF corectă, cu TVA-ul potrivit, trimisă pacientului pe email.',
    ],
    audience: 'Cabinete din România care trebuie să depună e-Factura și cabinete din UE care vor o factură corectă fără să se gândească la ea.',
    roadmap: {
      'spv-inbound': 'Preluarea facturilor de la furnizori din e-Factura, ca să nu mai bați niciuna la tastatură.',
      'fiscal-register': 'Integrare cu casa de marcat fiscală și imprimanta de bonuri.',
    },
    needs: [
      'Depunerea necesită înscrierea cabinetului cu propriul certificat calificat. Până atunci facturile sunt numerotate, generate și stocate — doar transmiterea așteaptă.',
    ],
  },
  commissions: {
    title: 'Comisioane medici',
    summary: 'Cât i se cuvine fiecărui colaborator, calculat de sistem, justificabil rând cu rând.',
    steps: [
      { title: 'Stabilești o dată', body: 'Un procent, o sumă fixă per manoperă sau ambele — per medic și per manoperă, unde diferă.' },
      { title: 'Se calculează singur', body: 'Fiecare manoperă finalizată reține înțelegerea care a guvernat-o, în ziua în care s-a întâmplat.' },
      { title: 'Plătești din situație', body: 'O situație per medic, per perioadă, cu lucrările din spatele fiecărui rând.' },
    ],
    details: [
      'Sfârșitul de lună încetează să fie o seară cu un tabel.',
      'Un procent nou convenit azi nu poate recalcula trimestrul trecut — inclusiv lunile deja plătite. Se aplică procentul în vigoare când s-a făcut lucrarea.',
      'Unui medic care contestă o cifră îi poți arăta pe loc manoperele din spatele ei.',
      'Procente diferite pentru manopere diferite, nu doar pentru medici diferiți — implanturile și controalele nu trebuie să împartă un procent.',
      'Cât aduce efectiv fiecare manoperă este net de ce plătește, pentru că comisionul intră în raportul de profitabilitate.',
    ],
    audience: 'Cabinete care plătesc colaboratorii cu un procent din ce aduc, nu cu salariu.',
    roadmap: {
      'payroll': 'Rularea salarizării și depunerea ei — momentan se calculează ce se datorează și atât.',
      'split': 'Împărțirea comisionului unei manopere între mai mulți medici.',
      'targets': 'Ținte și praguri de bonus, ca procentul să crească după depășirea unui prag.',
    },
  },

  reports: {
    title: 'Rapoarte',
    summary: 'Cât ai încasat, cât aduce efectiv fiecare manoperă și cine îți datorează.',
    steps: [
      { title: 'Alegi perioada', body: 'Orice interval, o locație sau toate.' },
      { title: 'Vezi unde sunt banii', body: 'Încasări în timp, pe locație, pe metodă de plată — și marja per manoperă.' },
      { title: 'Faci ceva cu asta', body: 'Lista datornicilor este o listă de sunat: cine cât datorează, cei mari primii.' },
    ],
    details: [
      'Profitul per manoperă, după materiale, comisionul medicului și timpul de scaun — ca să știi ce tratamente merită promovate și care te costă.',
      'Marja per oră de scaun, adică exact cifra care îți spune ce să pui în agendă.',
      'Lista datornicilor este o listă de sunat, nu un raport — cele mai mari solduri primele, cu număr de telefon.',
      'Încasări pe locație, ca un al doilea cabinet să fie judecat după cifrele lui.',
      'Cât te costă mesajele, pe canal, ca să nu devină discret un rând pe care nu îl poți explica.',
      'Proprietarii și managerii văd toate acestea. Medicii și recepția, nu.',
    ],
    audience: 'Proprietari și manageri care vor tabloul real fără să exporte nimic într-un tabel.',
    roadmap: {
      'utilisation': 'Analiză de productivitate per medic și de grad de ocupare a scaunelor.',
      'forecasting': 'Prognoză — proiectarea veniturilor în viitor, nu doar raportarea a ce s-a întâmplat.',
    },
    boundaries: [
      'Costul materialelor vine din materialele trecute la manoperă, deci una fără listă este costată doar pe comision și timp de scaun.',
    ],
  },

  // ── Pacienți & creștere ────────────────────────────────────────────────────
  'patient-portal': {
    title: 'Portal pacienți',
    summary: 'Pacienții își răspund singuri la întrebări, deci recepția nu mai răspunde ea.',
    steps: [
      { title: 'Îi inviți', body: 'Un clic din fișă. Acceptă cu un link — fără parolă inventată la ghișeu.' },
      { title: 'Se descurcă singuri', body: 'Programări, tratamente, schemă, radiografii și sold, de pe telefon.' },
      { title: 'Mai puține telefoane', body: '„Când sunt programat?” și „îmi trimiteți radiografia?” nu mai vin pe telefon.' },
    ],
    details: [
      'Cele două telefoane pe care recepția le primește cel mai des — când sunt programat, trimiteți-mi radiografia — pacientul și le rezolvă singur la zece seara.',
      'Se programează, anulează și reprogramează fără să ocupe pe nimeni la recepție.',
      'Plătesc ce datorează de pe telefon, ceea ce face să se încaseze mai mult.',
      'Un singur cont acoperă mai multe cabinete, deci un pacient al două locații ale tale este o persoană, nu două înregistrări.',
      'Își pot doar citi fișa clinică, niciodată modifica.',
      'Se instalează pe telefon ca o aplicație, în română sau engleză.',
    ],
    audience: 'Cabinete cu un flux constant de „când sunt programat” și „îmi trimiteți radiografia”.',
    boundaries: [
      'Un cont există doar când cabinetul invită pacientul. Nu există înscriere publică — nimeni nu trebuie să poată revendica o fișă medicală știind o adresă de email.',
      'Portalul este doar de citire în privința datelor clinice. Pacienții își văd fișa; nu o pot modifica.',
    ],
  },
  communications: {
    title: 'Mesaje către pacienți',
    summary: 'Reamintiri care pleacă singure, în limba pacientului, pe canalul la care răspunde.',
    steps: [
      { title: 'Alegi canalul', body: 'Fiecare pacient are unul pe care chiar îl citește. Mesajele îl urmează și încearcă altul dacă nu merge.' },
      { title: 'Scrii o singură dată', body: 'Șabloane per cabinet și per limbă, cu previzualizare înainte să plece ceva.' },
      { title: 'Uiți de asta', body: 'Confirmările și reamintirile la 24 și la o oră pleacă fără să își amintească nimeni.' },
    ],
    details: [
      'Scaunele goale din programări uitate dispar în bună măsură, fără ca recepția să dea telefoane.',
      'SMS, WhatsApp, email sau notificare web — care se citește efectiv, cu alternativă dacă nu merge.',
      'Muți sau anulezi o programare, iar reamintirile se aranjează singure. Nimeni nu trebuie să oprească vreuna.',
      'Pacienții primesc mesaje în limba lor și la ora locală a cabinetului.',
      'Confirmările, reamintirile și cererile de plată pleacă întotdeauna. Marketingul ajunge doar la cine a fost de acord.',
      'Fiecare mesaj de marketing are link de dezabonare, iar ce a acceptat cineva se consemnează cuvânt cu cuvânt.',
    ],
    audience: 'Orice cabinet care pierde scaune din programări uitate.',
    roadmap: {
      'two-way': 'Conversație în ambele sensuri, ca răspunsul pacientului să ajungă în aplicație, nu pe telefonul tău.',
    },
    boundaries: [
      'Costul mesajelor se scade dintr-un sold pre-alimentat, iar campaniile se opresc când acesta se termină, în loc să acumuleze o factură.',
    ],
    needs: [
      'Trimiterea are nevoie întâi de un cont de SMS și email conectat — te ajutăm să îl configurezi.',
    ],
  },
  recall: {
    title: 'Rechemări & recenzii',
    summary: 'Aduci pacienții înapoi înainte să alunece la altcineva.',
    steps: [
      { title: 'Observă', body: 'O verificare săptămânală găsește pe toți cei fără programare mai mult decât intervalul tău de rechemare.' },
      { title: 'Îi pune în ordine', body: 'Cei care merită contactați primii, ca bugetul limitat să nu se ducă alfabetic.' },
      { title: 'Îi contactezi', body: 'Din aplicație sau lași mesajul să plece singur.' },
    ],
    details: [
      'Pacienții care au încetat discret să vină sunt observați, în loc să apară peste doi ani la alt cabinet.',
      'Lista este ordonată după cine merită contactat, deci un buget mic de mesaje merge spre cei douăzeci de pacienți care contează, nu spre primii douăzeci alfabetic.',
      'Vezi de ce un pacient e sus în listă, deci este o judecată pe care o poți contrazice, nu un număr în care să ai încredere.',
      'Se oprește când soldul de mesaje s-ar termina și îți spune pe cine a sărit — fără facturi surpriză.',
      'Personalul primește un semnal gratuit în aplicație; mesajul efectiv către pacient este alegerea ta și se taxează.',
      'O cerere de recenzie pleacă la 48 de ore după o programare finalizată, cât încă își amintește cu plăcere.',
      'Scorul se calculează pe datele tale, în interiorul platformei. Nimic despre un pacient nu este trimis nicăieri pentru a decide pe cine contactezi întâi.',
    ],
    audience: 'Cabinete cu destui pacienți încât ținerea minte a încetat de mult să funcționeze.',
    boundaries: [
      'Mesajele de rechemare se scad din soldul de mesagerie, ca orice alt mesaj către pacient.',
    ],
  },
  'clinic-directory': {
    title: 'Director & programare online',
    summary: 'Un program care îți aduce pacienți, nu doar unul care îi administrează pe cei pe care îi ai.',
    steps: [
      { title: 'Îți publici profilul', body: 'Fotografii, medici, servicii, program, un punct pe hartă.' },
      { title: 'Ești găsit', body: 'Pacienții caută după oraș sau după tratament ori se uită pe hartă.' },
      { title: 'Se programează singuri', body: 'Direct în calendarul tău, la miezul nopții dacă atunci caută.' },
    ],
    details: [
      'Un pacient care nu a auzit de tine te găsește și se programează, fără să fie implicat cineva de la recepție.',
      'Se programează la ora la care chiar se gândește la asta, care rar este în programul tău.',
      'O locație nouă, fără reputație locală, este găsită după apropiere și tratament, nu după o vorbă bună pe care încă nu a câștigat-o.',
      'Pagina ta de profil este făcută să fie înțeleasă de motoarele de căutare, deci funcționează și în afara directorului nostru.',
      'Cineva fără cont devine fișă de pacient din nume și telefon chiar în momentul programării.',
      'Ceri un avans la programare, dacă lucrezi așa, ca programările online să fie la fel de serioase ca cele prin telefon.',
      'Listările nu sunt clasate sau promovate și nu există plasare plătită. Ce vede pacientul ține de apropiere și de ce a căutat, nu de cine a plătit.',
    ],
    audience: 'Cabinete cu scaune de umplut și locații noi, fără reputație locală încă.',
    roadmap: {
      'reviews': 'Colectarea și afișarea recenziilor în director. Momentan cererea de recenzie este un email care trimite unde alegi tu.',
    },
  },

  // ── Echipă & cabinet ───────────────────────────────────────────────────────
  'team-scheduling': {
    title: 'Programul echipei',
    summary: 'Vezi golurile de săptămâna viitoare săptămâna asta, cât mai poți face ceva.',
    steps: [
      { title: 'Spui de ce ai nevoie', body: 'Scaune per locație și câți oameni îți trebuie la recepție în același timp.' },
      { title: 'Fiecare își pune orele', body: 'Medicii și recepția își introduc singuri programul și își cer concediul.' },
      { title: 'Acoperi golurile din timp', body: 'Planningul arată unde lipsește acoperire, înainte să devină luni dimineață.' },
    ],
    details: [
      'Afli miercuri că vineri după-amiază e o singură persoană la recepție, nu vineri după-amiază.',
      'Scaunele fără medic și turele fără recepție se văd ca goluri, nu se descoperă ca probleme.',
      'O cerere de concediu în așteptare nu poate strica planningul viu — ultima versiune aprobată rămâne în vigoare până decide cineva.',
      'Ore contractate per persoană, deci vezi cine este sub normă și cine se îndreaptă spre ore suplimentare.',
      'Ai nevoie de cineva vineri? Ți se scrie o schiță de mesaj, o editezi și pleacă sub numele tău.',
      'Răspunsurile se întorc ca ture pe care le accepți cu un clic, deja ajustate la golul real și verificate față de concediile aprobate.',
      'Asistentul nu trimite niciodată mesaje personalului din proprie inițiativă. Un om îl declanșează, îl citește, iar mesajul pleacă sub numele lui.',
    ],
    audience: 'Cabinete cu destui angajați încât acoperirea să fie o problemă, nu o privire pe un tabel de perete.',
    boundaries: [
      'Orele de recepție sunt urmărite per cabinet, nu per locație.',
    ],
  },
  inventory: {
    title: 'Stocuri',
    summary: 'Știi ce e în dulap înainte să ai nevoie, nu în timp ce pacientul așteaptă.',
    steps: [
      { title: 'Listezi ce ai', body: 'Per locație, cu nivelul la care vrei să fii avertizat și cu expirarea, unde contează.' },
      { title: 'Se scade pe măsură ce lucrezi', body: 'Când notezi o manoperă se scade ce a consumat. Nimeni nu numără cutii vinerea.' },
      { title: 'Ești anunțat', body: 'Verificări zilnice pentru stoc scăzut și pentru ce urmează să expire.' },
    ],
    details: [
      'Rămasul fără material în mijlocul unei manopere încetează să se întâmple, pentru că ai fost avertizat cu zile înainte.',
      'Materialul expirat este prins cu 30 de zile înainte, nu găsit într-un sertar în timpul unei inspecții.',
      'Stocul se mișcă pe măsură ce notezi manoperele, deci cifra de pe ecran nu este o presupunere de la ultima inventariere.',
      'Fiecare modificare are un motiv în spate — vezi mereu de ce o cifră este cât este.',
      'Stocul se poate muta între locațiile tale, deci un cabinet rămas fără ceva nu înseamnă neapărat o comandă.',
      'Cât ai plătit pe fiecare articol intră în raportul de profitabilitate, deci costul materialelor din marje este real, nu estimat.',
    ],
    audience: 'Cabinete cu destul stoc încât rămasul fără material în timpul unei manopere să fie un risc real.',
    roadmap: {
      'price-library': 'O bibliotecă de prețuri de la furnizori pentru comparație, nu doar istoricul propriu de achiziții.',
      'purchase-orders': 'Emiterea comenzilor de achiziție din platformă.',
    },
    boundaries: [
      'Recepțiile intră din factura furnizorului, nu se tastează — vezi Facturi de la furnizori.',
    ],
  },

  // ── Platformă ──────────────────────────────────────────────────────────────
  'supplier-invoices': {
    title: 'Facturi de la furnizori',
    summary: 'O livrare devine stoc dintr-o singură trecere, în loc de o factură pe care nu o introduce nimeni.',
    steps: [
      { title: 'Deschizi factura', body: 'Liniile furnizorului, cu articolele tale de stoc sugerate alături.' },
      { title: 'Confirmi potrivirile', body: 'Spui ce este fiecare linie sau că nu e stoc deloc. O faci o singură dată per produs.' },
      { title: 'Recepționezi', body: 'Totul intră în stoc la locația potrivită, la prețul plătit efectiv.' },
    ],
    details: [
      'Luna viitoare aceleași produse de la același furnizor sosesc deja potrivite — munca se înjumătățește după prima factură.',
      'Cifrele de stoc nu mai fug, pentru că recepțiile nu se mai bat la tastatură și nu se mai sar când e aglomerat.',
      'Recepționezi la prețul plătit efectiv, deci marjele tale se construiesc pe cost real, nu pe cel de anul trecut.',
      'O factură nu poate fi recepționată de două ori, deci o după-amiază agitată nu îți poate dubla stocul pe hârtie.',
      'Același produs poate corespunde altui articol în fiecare locație, pentru că denumirile pot diferi de la cabinet la cabinet.',
    ],
    audience: 'Cabinete cărora le-au fugit cifrele de stoc pentru că recepțiile se bat la tastatură, sau deloc.',
    roadmap: {
      'efactura-pull': 'Preluarea automată a facturilor din e-Factura, în loc să fie parcurse în aplicație.',
      'purchase-orders': 'Comenzi de achiziție și potrivirea facturii cu comanda și cu recepția.',
      'price-library': 'O bibliotecă de prețuri față de care să verifici cifrele unui furnizor.',
    },
  },

  ai: {
    title: 'Inteligență artificială',
    summary: 'Nu mai tastezi notițe. Știi cine nu va veni. Contactezi întâi pacienții potriviți.',
    steps: [
      { title: 'Dictezi vizita', body: 'O spui cu voce tare. Se întoarce ca notițe și câmpuri completate, pe care le verifici înainte de salvare.' },
      { title: 'Doar întrebi', body: 'Programezi, cauți și afli scriind sau vorbind, în loc să dai clic prin ecrane.' },
      { title: 'Învață cabinetul tău', body: 'Predicția neprezentărilor se antrenează pe pacienții tăi, nu pe stomatologie în general.' },
    ],
    details: [
      'Dictezi consultația în loc să o tastezi și petreci programarea uitându-te la pacient.',
      'Afli care dintre programările de mâine riscă să fie ratate, la timp cât să faci ceva.',
      'Predicția învață din pacienții tăi și din istoricul tău — nu dintr-un model general construit pe cabinetul altcuiva.',
      'Lista de rechemare este pusă în ordinea celor care merită contactați, deci un buget mic merge unde aduce.',
      'Textele de campanie ți se schițează pe segmente; un om le citește și decide să le trimită.',
      'Nimic nu se aplică automat. Dictarea, recomandările și schițele sunt toate verificate de un om înainte să conteze.',
      'Acolo unde se folosește un model extern, acesta nu vede niciodată numele, CNP-ul sau datele de contact ale pacientului.',
      'Înregistrarea audio a dictării se șterge după șapte zile. Textul rămâne cât spune politica ta de retenție; înregistrarea nu.',
    ],
    audience: 'Cabinete în care medicul petrece programarea tastând, în loc să se uite la pacient.',
    needs: [
      'Funcțiile AI trebuie pornite pentru cabinetul tău. Până atunci sunt pur și simplu oprite, iar restul funcționează exact la fel.',
    ],
  },
  security: {
    title: 'Securitate & datele tale',
    summary: 'Răspunzi cu încredere la întrebarea despre protecția datelor și pleci cu tot, dacă vrei vreodată.',
    steps: [
      { title: 'Fișele tale sunt doar ale tale', body: 'Alt cabinet de pe platformă nu ajunge la ele. Este impus, nu promis.' },
      { title: 'Consimțământul este real', body: 'Nu o bifă — exact formularea cu care a fost cineva de acord, păstrată permanent.' },
      { title: 'Poți pleca', body: 'Exporți tot, cu fișiere cu tot, oricând vrei.' },
    ],
    details: [
      'Când un pacient întreabă ce dețineți despre el, poți răspunde în aceeași zi, nu promite că verifici.',
      'Un pacient care cere să fie uitat este un buton: persoana este anonimizată, iar fișa clinică pe care legea te obligă să o păstrezi supraviețuiește.',
      'Poți pleca cu tot — fișe și radiografiile și documentele reale, nu o listă de nume de fișiere. Nimeni nu este ținut de propriile date.',
      'Datele de contact și CNP-ul sunt criptate în bază, deci o copie furată nu este o listă de pacienți furată.',
      'Alt cabinet de pe platformă nu îți poate vedea fișele nici din greșeală — limita se aplică automat la fiecare interogare.',
      'Acordul pentru reamintiri și pentru marketing se consemnează cu formularea folosită și cu momentul în care a fost dat.',
      'Tot ce face oricine se scrie într-un jurnal care nu mai poate fi modificat ulterior.',
      'Exporturile expiră după 14 zile, iar arhiva se șterge atunci — o copie a tot ce deții nu trebuie să rămână la nesfârșit.',
    ],
    audience: 'Oricine trebuie să răspundă la o întrebare despre protecția datelor — adică, pentru un cabinet dentar, toată lumea.',
    boundaries: [
      'Exportul complet se generează în fundal — cu cât ai mai mult, cu atât durează mai mult.',
    ],
  },
  integrations: {
    title: 'Integrări & API',
    summary: 'Programările tale în calendarul pe care deja îl folosești și o cale să conectezi ce mai ai.',
    steps: [
      { title: 'Conectezi un calendar', body: 'Medicul leagă Google sau Outlook o dată. De atunci, programările lui apar acolo.' },
      { title: 'Viața lui îți protejează agenda', body: 'Timpul ocupat din calendarul personal blochează programările aici — gata cu vinerile suprapuse.' },
      { title: 'Construiești pe el, dacă vrei', body: 'Webhook-uri și un API documentat pentru programul contabilului sau scriptul tău.' },
    ],
    details: [
      'Medicul își vede ziua de cabinet în calendarul pe care îl are deja pe telefon, fără să verifice în două locuri.',
      'O obligație personală din calendarul lui blochează intervalul aici, deci nimeni nu îl programează peste serbarea copilului.',
      'Fluxul de calendar nu conține date despre pacient — o programare apare ca o programare, adică exact ce trebuie să arate un ecran de telefon văzut de altcineva.',
      'Celelalte tale sisteme pot fi anunțate când se întâmplă ceva, în loc să întrebe periodic, iar mesajele sunt semnate ca să poată fi crezute.',
      'Un webhook care eșuează este reîncercat de trei ori și apoi oprit, în loc să bată la nesfârșit într-o adresă moartă.',
      'Cheile de API sunt limitate la ce le trebuie și stocate astfel încât să nu poată fi citite înapoi — nici de noi.',
    ],
    audience: 'Cabinete cu programul unui contabil, un instrument de marketing sau un script propriu de alimentat.',
    roadmap: {
      'accounting': 'Conexiuni gata făcute către pachetele de contabilitate obișnuite, ca să nu fie nevoie să le construiască cineva.',
    },
    needs: [
      'Sincronizarea calendarului trebuie pornită pentru cabinet, apoi fiecare medic își conectează propriul calendar.',
    ],
  },
};
